<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import type {
  ISatPlaceSearcherEmits,
  ISatButtonCurrentLocationEmits,
} from "@chatfood/design-system";
import {
  SatPlaceSearcher,
  SatButtonCurrentLocation,
} from "@chatfood/design-system";
import { ref, watch } from "vue";
import { useLocale } from "~/composables/use-locale";
import { usePublicConfig } from "~/config";
import { useBusinessStore } from "~/stores/business";
import type { INewAddressProps } from "~/components/new-address/props";
import type { INewAddressEmits } from "~/components/new-address/emits";
import { useDelivery } from "~/composables/use-delivery";

const props = defineProps<INewAddressProps>();
const emits = defineEmits<INewAddressEmits>();

const css = bemBuilder("new-address");
const { t } = useLocale();
const { googleMapsApiKey } = usePublicConfig();
const { getCoordinateCoverage } = useDelivery();

const business = useBusinessStore();

const onSelect = async (place: ISatPlaceSearcherEmits["select"][0]) => {
  const coverage = await getCoordinateCoverage(place.coordinates);
  if (!coverage) {
    emits("uncoveredAddressSelected", {
      ...place,
      canDeliver: false,
    });
    return;
  }

  emits("select", {
    ...place,
    canDeliver: true,
    deliveryCoverage: coverage,
  });
};

const initialValue = ref(props.activeAddress ?? "");
watch(
  () => props.activeAddress,
  (val) => {
    initialValue.value = val ?? "";
  },
);

const onCurrentLocation = async (
  location: ISatButtonCurrentLocationEmits["success"][0],
) => {
  const coverage = await getCoordinateCoverage(location.coordinates);
  if (!coverage) {
    emits("uncoveredAddressSelected", {
      ...location,
      canDeliver: false,
    });
    return;
  }

  emits("select", {
    ...location,
    canDeliver: true,
    deliveryCoverage: coverage,
  });
};
</script>

<template>
  <div :class="css('')">
    <SatPlaceSearcher
      :api-key="googleMapsApiKey"
      :country="business.country"
      :input="initialValue"
      :max="4"
      :placeholder="t('components.new_address.search_input_placeholder')"
      @select="onSelect"
    />
    <SatButtonCurrentLocation
      :class="css('current-location')"
      :label="t('components.new_address.current_location')"
      :wide="true"
      :api-key="googleMapsApiKey"
      @success="onCurrentLocation"
    />
  </div>
</template>

<style lang="scss" scoped>
.new-address {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}
</style>
